import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, keyframes, styled } from '@mui/system';

import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';

import useConfirm from '../../hooks/useConfirm';
import { getHeatBuzz } from '../../common/ItemTools';
import { sourceLogo, sourceName } from '../common/sources';
import {
  BORDER, ERROR, HAIRLINE, HOVER_BG, LINK_BLUE, MUTED, SANS_STACK, TEXT_BODY,
  TEXT_SECONDARY,
} from '../common/tokens';
import { SectionLabel, TextInput } from '../common/hero/styles';
import { formatDate } from '../common/hero/utils';
import { languageName, typeLabel } from './utils';

// Formatters shared by both table shapes.
const num = (v) => (v == null ? '' : Number(v).toLocaleString());
const date = (v) => formatDate(v);
const list = (v) => (v && v.length ? v.join(', ') : '');
const yesNo = (v) => (v ? 'Yes' : '');
const megabytes = (v) => (v == null ? '' : `${(v / 1024).toFixed(2)} MB`);

// Every field a post can carry, in the order its column falls. A post source
// declares only what the table can't derive — the id column's label and link,
// a label where the shared name reads wrong for that source, and which fields a
// moderator may edit. A field a post carries that isn't listed still gets a
// column, after these, under its own key.
const POST_FIELDS = [
  { field: 'title', label: 'Title', prose: true },
  { field: 'points', label: 'Points', numeric: true, format: num },
  { field: 'score', label: 'Score', numeric: true, format: num },
  { field: 'num_comments', label: 'Comments', numeric: true, format: num },
  { field: 'ups', label: 'Ups', numeric: true, format: num },
  { field: 'downs', label: 'Downs', numeric: true, format: num },
  { field: 'upvote_ratio', label: 'Ratio', numeric: true, format: (v) => (v == null ? '' : v.toFixed(2)) },
  { field: 'likes', label: 'Vote' },
  { field: 'author', label: 'Author' },
  { field: 'submitter_user', label: 'Submitter' },
  { field: 'subreddit_name_prefixed', label: 'Subreddit' },
  { field: 'subreddit', label: 'Subreddit name' },
  { field: 'subreddit_subscribers', label: 'Subscribers', numeric: true, format: num },
  { field: 'created_at_i', label: 'Posted', numeric: true, format: date },
  { field: 'created_utc', label: 'Posted', numeric: true, format: date },
  { field: 'created_at', label: 'Posted', numeric: true, format: date },
  { field: 'domain', label: 'Domain' },
  { field: 'is_self', label: 'Self post', format: yesNo },
  { field: 'is_video', label: 'Video', format: yesNo },
  { field: 'permalink', label: 'Permalink', prose: true },
  { field: 'thumbnail', label: 'Thumbnail', prose: true },
  { field: 'selftext', label: 'Text', prose: true },
];

// The sources that report individual posts, one table each. `postUrl` builds the
// link on the post id; `labels` renames a shared field where the source means
// something else by it - X counts likes and replies, not points and comments.
// `fields` are the ones a moderator may edit.
// Field names are the ones `enhanceUrlFrom*` writes — see docs/url-enhancement.md.
const POST_SOURCES = [
  {
    key: 'hn',
    idLabel: 'Story',
    postUrl: (id) => `https://news.ycombinator.com/item?id=${id}`,
    fields: ['title', 'points', 'num_comments', 'author', 'created_at_i'],
  },
  {
    key: 'rd',
    idLabel: 'Post',
    postUrl: (id, post) => (post.permalink ? `https://reddit.com${post.permalink}` : `https://redd.it/${id}`),
    fields: ['title', 'score', 'num_comments', 'author', 'created_utc'],
  },
  {
    key: 'lb',
    idLabel: 'Story',
    postUrl: (id) => `https://lobste.rs/s/${id}`,
    fields: ['title', 'score', 'num_comments', 'submitter_user', 'created_at'],
  },
  {
    key: 'tw',
    idLabel: 'Tweet',
    postUrl: (id, post) => `https://x.com/${post.author || 'i'}/status/${id}`,
    labels: { points: 'Likes', num_comments: 'Replies' },
    fields: ['points', 'num_comments', 'author', 'created_at'],
  },
];

// The sources that cache one record per URL rather than a set of posts. A row
// whose value comes back empty is dropped, so a sparse record stays short.
const RECORD_SOURCES = [
  {
    key: 'gh',
    rows: [
      { label: 'Repository', get: (d) => d.fullName },
      { label: 'Owner', get: (d) => d.owner },
      { label: 'Stars', get: (d) => d.stats?.stars, format: num },
      { label: 'Forks', get: (d) => d.stats?.forks, format: num },
      { label: 'Watchers', get: (d) => d.stats?.watchers, format: num },
      { label: 'Open issues', get: (d) => d.stats?.openIssues, format: num },
      { label: 'Created', get: (d) => d.createdAt, format: date },
      { label: 'Updated', get: (d) => d.updatedAt, format: date },
      { label: 'Last push', get: (d) => d.pushedAt, format: date },
      { label: 'First commit', get: (d) => d.firstCommitDate, format: date },
      { label: 'Language', get: (d) => d.language },
      { label: 'All languages', get: (d) => d.languages, format: list },
      { label: 'Topics', get: (d) => d.topics, format: list },
      { label: 'License', get: (d) => d.license },
      { label: 'Homepage', get: (d) => d.homepage, link: (d) => d.homepage },
      { label: 'Default branch', get: (d) => d.defaultBranch },
      { label: 'Size', get: (d) => d.size, format: megabytes },
      { label: 'Fork', get: (d) => d.isFork || null, format: yesNo },
      { label: 'Archived', get: (d) => d.isArchived || null, format: yesNo },
      { label: 'Template', get: (d) => d.isTemplate || null, format: yesNo },
    ],
  },
  {
    key: 'yt',
    rows: [
      { label: 'Views', get: (d) => d.stats?.viewCount, format: num },
      { label: 'Likes', get: (d) => d.stats?.likeCount, format: num },
      { label: 'Comments', get: (d) => d.stats?.commentCount, format: num },
      { label: 'Subscribers', get: (d) => d.subscriberCount, format: num },
      { label: 'Category', get: (d) => d.category },
      { label: 'Uploaded', get: (d) => d.uploadDate, format: date },
      { label: 'Tags', get: (d) => d.tags, format: list },
    ],
  },
  {
    key: 'ai',
    rows: [
      { label: 'Explanation', get: (d) => d.explanation, wrap: true },
    ],
  },
];

// The web record is a container of suggestions rather than one origin's record.
// It renders as its own shape and takes a heading of its own — `sourceName`
// speaks for the origin inside it, the page's meta tags.
const WEB = 'web';
const BLOCK_LABELS = { [WEB]: 'Page metadata' };

// The order sources render in - the scored ones in `METRIC_PATHS` order, then the
// unscored, matching the card's expand panel. A key the blob carries that isn't
// listed still renders, generically, after these.
const SOURCE_ORDER = [WEB, 'hn', 'yt', 'gh', 'tw', 'rd', 'lb', 'ai'];

// The item's rolled-up metrics, per source, as `aggregateItemStats` writes them
// under `extra.stats`. The ones heat and buzz weigh have a `<metric>Norm` beside
// them.
const SCORE_METRICS = {
  hn: [['popularity', 'Points'], ['engagement', 'Comments'], ['numPosts', 'Posts']],
  rd: [['popularity', 'Score'], ['engagement', 'Comments'], ['numPosts', 'Posts']],
  lb: [['popularity', 'Score'], ['engagement', 'Comments'], ['numPosts', 'Posts']],
  tw: [['popularity', 'Likes'], ['engagement', 'Replies'], ['numPosts', 'Posts']],
  gh: [['stars', 'Stars'], ['forks', 'Forks'], ['watchers', 'Watchers'], ['issues', 'Open issues']],
  yt: [['views', 'Views'], ['likes', 'Likes'], ['comments', 'Comments']],
};

// The link types `aggregateItemStats` promotes a top discussion under.
const DISCUSSION_LINK_SOURCES = {
  1: 'hn', 2: 'lb', 5: 'tw', 6: 'rd',
};

// How to call suggestion fields. An unnamed field falls back to its own key
// rather than going missing.
const SUGGESTION_LABELS = {
  title: 'Title',
  excerpt: 'Excerpt',
  type: 'Type',
  lang: 'Language',
  timePublished: 'Published',
  timeNeeded: 'Read time',
  wordCount: 'Words',
  coverImageUrl: 'Cover image',
  name: 'Name',
  username: 'Username',
  imageUrl: 'Image',
  url: 'URL',
};

const SUGGESTION_FORMATS = {
  type: typeLabel,
  lang: languageName,
  timePublished: date,
  timeNeeded: (v) => `${v} min`,
  wordCount: num,
};

// Fields that carry prose or a URL, and so need a measure rather than a column.
const WRAPPED_FIELDS = ['title', 'excerpt', 'coverImageUrl', 'imageUrl', 'url'];

const INPUT_WIDTHS = {
  link: 280, prose: 240, date: 130, number: 80, text: 120,
};

const LINK_PLACEHOLDER = 'Paste a Hacker News, Reddit, Lobsters or X post link';

const Toggle = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: SANS_STACK,
});

const Scroller = styled(Box)({
  overflowX: 'auto',
  marginTop: 10,
});

const Table = styled('table')({
  borderCollapse: 'collapse',
  fontFamily: SANS_STACK,
  fontSize: '.82rem',
  color: TEXT_BODY,
  whiteSpace: 'nowrap',
});

const Th = styled('th')({
  padding: '6px 14px 6px 0',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: MUTED,
  borderBottom: `1px solid ${HAIRLINE}`,
});

const SortTh = styled(Th)({
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': { color: TEXT_SECONDARY },
});

const Td = styled('td')({
  padding: '6px 14px 6px 0',
  borderBottom: `1px solid ${HAIRLINE}`,
});

// The property column of a record table — a label rather than a value, so it
// takes the header's styling in a body row.
const LabelTd = styled(Td)({
  paddingRight: 20,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: MUTED,
});

// One source's block. Inside a URL's nest the blocks are peers, and spacing
// alone let a short one read as part of the one above.
const SourceBox = styled(Box)({
  '& + &': {
    marginTop: 20,
    paddingTop: 16,
    borderTop: `1px solid ${HAIRLINE}`,
  },
});

// Which kind of thing the cards under it are. Takes the brand coral and a rule
// of its own.
const SubLabel = styled(Box)(({ theme }) => ({
  marginTop: 20,
  paddingBottom: 4,
  borderBottom: `1px solid ${HAIRLINE}`,
  fontFamily: SANS_STACK,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: theme.palette.primary.main,
}));

// A post's prose — a title, a self-post body — inside a grid of figures: give it
// a measure and cut it off after three lines, with the whole value on hover. It
// clamps inside the cell rather than on it, since the clamp needs a display mode
// a table cell can't take.
const Clamp = styled('span')({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  whiteSpace: 'normal',
  maxWidth: 340,
  minWidth: 180,
  lineHeight: 1.45,
});

// One suggestion, bounded. A record of a page's title and a record of one of
// its authors are separate claims, and hairline rows alone ran them together.
const Card = styled(Box)({
  border: `1px solid ${BORDER}`,
  borderRadius: 6,
  padding: '9px 12px 6px',
  maxWidth: '100%',
  overflow: 'hidden',
  // The card's own edge closes the last row; the row's rule would double it.
  '& tbody tr:last-of-type td': { borderBottom: 'none' },
  '& > :last-child': { marginTop: 5 },
});

// Cards sit side by side where they fit — two short author records read as two
// entities on one line, where stacked they read as more of the same list.
const CardRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 10,
  marginTop: 8,
});

// A card's header: which origin is making this claim.
const OriginLabel = styled(Box)({
  fontFamily: SANS_STACK,
  fontSize: '.78rem',
  fontWeight: 700,
  color: TEXT_SECONDARY,
});

// Prose doesn't belong in the table's nowrap grid — it needs a measure to read at.
const WrapTd = styled(Td)({
  whiteSpace: 'normal',
  maxWidth: 620,
  lineHeight: 1.6,
  paddingTop: 10,
  paddingBottom: 10,
});

const Row = styled('tr')({
  '&:hover td': { background: HOVER_BG },
});

const Link = styled('a')({
  color: LINK_BLUE,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
});

// The URL a section speaks for. Long enough to need a measure and a break,
// since a query string has nowhere to wrap.
const UrlLink = styled('a')({
  display: 'inline-block',
  fontFamily: SANS_STACK,
  fontSize: '.8rem',
  fontWeight: 600,
  color: LINK_BLUE,
  textDecoration: 'none',
  maxWidth: 620,
  overflowWrap: 'anywhere',
  '&:hover': { textDecoration: 'underline' },
});

// A URL whose entity went missing: its id is all we can name it by.
const MissingUrl = styled(Box)({
  fontFamily: SANS_STACK,
  fontSize: '.8rem',
  fontWeight: 600,
  color: MUTED,
});

// An action on the open section: re-read the page, plainly or with AI. Muted
// like the chevron beside it — the tables are the page's content, not this.
const Action = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 6px',
  border: `1px solid ${HAIRLINE}`,
  borderRadius: 4,
  background: 'none',
  cursor: 'pointer',
  fontFamily: SANS_STACK,
  fontSize: '.7rem',
  letterSpacing: '.03em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: MUTED,
  '&:hover': { color: TEXT_SECONDARY, background: HOVER_BG },
  '&:disabled': { cursor: 'default', opacity: 0.5 },
});

// A post row's own actions — quieter than the section's, since every row has them.
const RowAction = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  padding: 2,
  border: 'none',
  borderRadius: 4,
  background: 'none',
  cursor: 'pointer',
  color: MUTED,
  '&:hover': { color: TEXT_SECONDARY, background: HOVER_BG },
  '&:disabled': { cursor: 'default', opacity: 0.5 },
});

const ActionsTd = styled(Td)({
  paddingRight: 8,
});

// The hero edit field, shrunk to sit in a table row.
const CellInput = styled(TextInput, {
  shouldForwardProp: (prop) => prop !== 'measure',
})(({ measure }) => ({
  width: measure,
  maxWidth: '100%',
  padding: '3px 6px',
  borderRadius: 4,
  fontSize: '.82rem',
}));

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// What a folded section holds, named so it can be left folded.
const Folded = styled(Box)({
  fontFamily: SANS_STACK,
  fontSize: '.75rem',
  color: MUTED,
});

// Holds a URL's sources under its heading, so a source reads as belonging to
// the page above it rather than to the item.
const Nest = styled(Box)({
  marginTop: 12,
  paddingLeft: 14,
  borderLeft: `1px solid ${HAIRLINE}`,
});

const Note = styled(Box)({
  marginTop: 12,
  fontFamily: SANS_STACK,
  fontSize: '.85rem',
  color: TEXT_SECONDARY,
});

// Dates edit by day, in local time, the way `formatDate` shows them.
function inputType(column) {
  if (column.format === date) return 'date';
  return column.numeric ? 'number' : 'text';
}

function toInput(column, value) {
  if (value == null) return '';
  return inputType(column) === 'date' ? moment(value * 1000).format('YYYY-MM-DD') : String(value);
}

function fromInput(column, text) {
  return inputType(column) === 'date' && text ? moment(text, 'YYYY-MM-DD').unix() : text;
}

// Esc from a field is handled on the field; this catches the one that lands on
// the page once something (Vimium) has blurred it.
function useEscape(onEscape) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        onEscape();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onEscape]);
}

// Sorts strings case-insensitively and everything else numerically, so a
// missing value sorts as empty rather than throwing the order out.
function compareBy(field, order) {
  const direction = order === 'asc' ? 1 : -1;
  return (a, b) => {
    const av = a[field];
    const bv = b[field];
    const [x, y] = typeof av === 'string' || typeof bv === 'string'
      ? [String(av || '').toLowerCase(), String(bv || '').toLowerCase()]
      : [av || 0, bv || 0];
    if (x < y) return -direction;
    if (x > y) return direction;
    return 0;
  };
}

// Where a failed fetch left its message. AI and the web fetch record one under
// `stats`, every other source at the top level; the web fetch records a bare
// HTTP status where it has one.
function sourceError(data) {
  const error = data.fetchError || data.stats?.error;
  if (error == null) return null;
  return typeof error === 'number' ? `HTTP ${error}` : error;
}

// Which posts a refresh couldn't read, and why; they keep what they had.
function keptMessage(sourceKey, kept) {
  const idsByError = {};
  kept.forEach(({ id, error }) => {
    idsByError[error] = [...(idsByError[error] || []), id];
  });
  const reasons = Object.entries(idsByError)
    .map(([error, ids]) => `${ids.join(', ')} (${error})`)
    .join('; ');
  return `${sourceName(sourceKey)}: couldn't refresh ${reasons} — kept as ${kept.length === 1 ? 'it was' : 'they were'}`;
}

// Resolves a record source against one cached record, dropping every row the
// record has nothing for — a record that only carries its refresh stamp
// resolves to none, and shows as a heading alone.
function recordRows(source, data) {
  return source.rows
    .map((row) => ({ ...row, value: row.get(data) }))
    .filter((row) => row.value != null && row.value !== '')
    .map((row) => ({ ...row, text: row.format ? row.format(row.value) : row.value }))
    .filter((row) => row.text !== '');
}

// One stored suggestion, read off the record rather than declared field by
// field, so a field the pipeline adds later still shows. `src` is the origin
// column, not a row of its own.
function suggestionRows(suggestion) {
  return Object.entries(suggestion)
    .filter(([field]) => field !== 'src')
    .map(([field, value]) => ({
      field,
      label: SUGGESTION_LABELS[field] || field,
      text: SUGGESTION_FORMATS[field] ? SUGGESTION_FORMATS[field](value) : String(value ?? ''),
      wrap: WRAPPED_FIELDS.includes(field),
    }))
    .filter((row) => row.text !== '');
}

// What tells one of a record's suggestions from the next: the origin, plus what
// the suggestion names, since an origin can offer several.
function suggestionKey(suggestion) {
  const origin = suggestion.src || WEB;
  // A cover image tells one of a page's image suggestions from the next, where
  // every other field on them is the same.
  const named = [
    suggestion.name, suggestion.username, suggestion.url,
    suggestion.title, suggestion.coverImageUrl,
  ].filter(Boolean).join('|');
  return named ? `${origin}-${named}` : origin;
}

// A source with no shape declared above — one added to the pipeline before this
// file caught up. `stats` is the heading's business and `posts` needs a table,
// so both stay out of it.
function genericRows(data) {
  return Object.entries(data)
    .filter(([field]) => field !== 'stats' && field !== 'posts')
    .map(([field, value]) => ({
      label: field,
      text: Array.isArray(value) ? list(value) : String(value ?? ''),
      wrap: true,
    }))
    .filter((row) => row.text !== '');
}

// The columns one set of posts earns: every declared field any of them carries
// or a moderator may edit, in declared order, then whatever else they carry
// under its own key.
function postColumns(source, posts) {
  const present = new Set(source.fields);
  Object.values(posts).forEach((post) => Object.keys(post).forEach((field) => present.add(field)));

  const declared = POST_FIELDS.filter((column) => present.has(column.field));
  declared.forEach((column) => present.delete(column.field));
  const rest = [...present].sort().map((field) => ({ field, label: field }));

  return [...declared, ...rest]
    .map((column) => ({ ...column, label: source.labels?.[column.field] || column.label }));
}

// What one URL recorded, declared sources in order and the rest after. A
// record's existence is itself a finding — a gh record means the URL is a repo,
// a bare hn record means HN was searched and had nothing — so a source shows
// even when it resolves to a heading alone.
// What a source's block is called — the web record is a container of several
// origins, so it takes a name of its own.
function blockLabel(key) {
  return BLOCK_LABELS[key] || sourceName(key);
}

function orderedSources(extra) {
  const keys = Object.keys(extra);
  return [
    ...SOURCE_ORDER.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !SOURCE_ORDER.includes(key)),
  ];
}

function SourceHeading({ sourceKey, data }) {
  const logo = sourceLogo(sourceKey);
  const refreshedAt = data.stats?.refreshedAt;
  const error = sourceError(data);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: '7px', flexWrap: 'wrap',
    }}>
      {logo && <img src={logo} alt="" style={{ width: 15, height: 15, alignSelf: 'center' }} />}
      <Box sx={{
        fontFamily: SANS_STACK, fontSize: '.85rem', fontWeight: 700, color: TEXT_BODY,
      }}>
        {blockLabel(sourceKey)}
      </Box>
      {refreshedAt && (
        <Box sx={{ fontFamily: SANS_STACK, fontSize: '.75rem', color: MUTED }}>
          {`refreshed ${formatDate(refreshedAt)}`}
        </Box>
      )}
      {error && (
        <Box sx={{ fontFamily: SANS_STACK, fontSize: '.75rem', color: ERROR }}>
          {error}
        </Box>
      )}
    </Box>
  );
}

SourceHeading.propTypes = {
  sourceKey: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
};

// One post, open for editing. Saves only what changed, so a date the field
// shows by day keeps its time unless it was touched.
function EditRow({
  source, post, columns, onSave, onCancel,
}) {
  const firstField = columns.find((c) => source.fields.includes(c.field))?.field;
  const [initial] = useState(() => ({
    link: source.postUrl(post.id, post),
    ...Object.fromEntries(columns
      .filter((c) => source.fields.includes(c.field))
      .map((c) => [c.field, toInput(c, post[c.field])])),
  }));
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(null);
  useEscape(onCancel);

  const save = () => {
    const patch = Object.fromEntries(Object.keys(values)
      .filter((key) => values[key] !== initial[key])
      .map((key) => {
        const column = columns.find((c) => c.field === key);
        return [key, column ? fromInput(column, values[key]) : values[key]];
      }));
    if (!Object.keys(patch).length) {
      onCancel();
      return;
    }
    setSaving(true);
    setFailed(null);
    onSave(post.id, patch).catch((e) => {
      setFailed(e.message);
      setSaving(false);
    });
  };

  // Enter on the row's own buttons is theirs — on Cancel it must not save.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const field = (key, type, measure, label) => (
    <CellInput
      type={type}
      measure={measure}
      value={values[key]}
      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
      disabled={saving}
      aria-label={label}
      autoFocus={key === firstField}
    />
  );

  return (
    <Row onKeyDown={onKeyDown}>
      <ActionsTd>
        <RowAction type="button" onClick={save} disabled={saving} title="Save (Enter)">
          <CheckIcon sx={{ fontSize: 16 }} />
        </RowAction>
        <RowAction type="button" onClick={onCancel} disabled={saving} title="Cancel (Esc)">
          <CloseIcon sx={{ fontSize: 16 }} />
        </RowAction>
        {failed && <Folded sx={{ color: ERROR, whiteSpace: 'normal', maxWidth: 200 }}>{failed}</Folded>}
      </ActionsTd>
      <Td>{field('link', 'url', INPUT_WIDTHS.link, `${source.idLabel} link`)}</Td>
      {columns.map((c) => {
        if (!source.fields.includes(c.field)) {
          return <Td key={c.field}>{String(c.format ? c.format(post[c.field]) : (post[c.field] ?? ''))}</Td>;
        }
        const type = inputType(c);
        return (
          <Td key={c.field}>
            {field(c.field, type, c.prose ? INPUT_WIDTHS.prose : INPUT_WIDTHS[type], c.label)}
          </Td>
        );
      })}
    </Row>
  );
}

EditRow.propTypes = {
  source: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

function RefreshGlyph({ spinning }) {
  return (
    <RefreshIcon sx={{ fontSize: 16, ...(spinning && { animation: `${spin} 1s linear infinite` }) }} />
  );
}

RefreshGlyph.propTypes = { spinning: PropTypes.bool.isRequired };

// Stands for every post in `busy`, while the whole table is being refreshed.
const ALL_POSTS = '*';

function PostTable({
  source, posts, editingId, onEdit, onSave, onRemove, onRefresh,
}) {
  const columns = postColumns(source, posts);
  const firstNumeric = columns.find((c) => c.numeric) || columns[0];
  const [sort, setSort] = useState({ field: firstNumeric.field, order: 'desc' });
  // The post an action is running on, as `{ id, action }`.
  const [busy, setBusy] = useState(null);

  const toggleSort = (field) => setSort((s) => (
    s.field === field ? { field, order: s.order === 'asc' ? 'desc' : 'asc' } : { field, order: 'desc' }
  ));

  const run = (id, action, call) => {
    setBusy({ id, action });
    call().finally(() => setBusy(null));
  };
  const isBusy = (id) => Boolean(busy) && (busy.id === id || busy.id === ALL_POSTS);
  const isRefreshing = (id) => isBusy(id) && busy.action === 'refresh';

  const rows = Object.entries(posts)
    .map(([id, post]) => ({ id, ...post }))
    .sort(compareBy(sort.field, sort.order));

  return (
    <Scroller>
      <Table>
        <thead>
          <tr>
            <Th aria-label="Actions">
              <RowAction
                type="button"
                onClick={() => run(ALL_POSTS, 'refresh', () => onRefresh())}
                disabled={Boolean(busy)}
                title={`Refresh every post from ${sourceName(source.key)}`}
              >
                <RefreshGlyph spinning={busy?.id === ALL_POSTS} />
              </RowAction>
            </Th>
            <Th>{source.idLabel}</Th>
            {columns.map((c) => (
              <SortTh key={c.field} onClick={() => toggleSort(c.field)}>
                {c.label}
                {sort.field === c.field && (sort.order === 'asc' ? ' ▲' : ' ▼')}
              </SortTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((post) => (post.id === editingId ? (
            <EditRow
              key={post.id}
              source={source}
              post={post}
              columns={columns}
              onSave={onSave}
              onCancel={() => onEdit(null)}
            />
          ) : (
            <Row key={post.id}>
              <ActionsTd>
                <RowAction
                  type="button"
                  onClick={() => run(post.id, 'refresh', () => onRefresh(post.id))}
                  disabled={isBusy(post.id)}
                  title={`Refresh from ${sourceName(source.key)}`}
                >
                  <RefreshGlyph spinning={isRefreshing(post.id)} />
                </RowAction>
                <RowAction
                  type="button"
                  onClick={() => onEdit(post.id)}
                  disabled={isBusy(post.id)}
                  title="Edit"
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </RowAction>
                <RowAction
                  type="button"
                  onClick={() => run(post.id, 'remove', () => onRemove(post.id))}
                  disabled={isBusy(post.id)}
                  title="Remove"
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </RowAction>
              </ActionsTd>
              <Td>
                <Link href={source.postUrl(post.id, post)} target="_blank" rel="noopener noreferrer">
                  {post.id}
                </Link>
              </Td>
              {columns.map((c) => {
                const text = String(c.format ? c.format(post[c.field]) : (post[c.field] ?? ''));
                return (
                  <Td key={c.field}>
                    {c.prose ? <Clamp title={text}>{text}</Clamp> : text}
                  </Td>
                );
              })}
            </Row>
          )))}
        </tbody>
      </Table>
    </Scroller>
  );
}

PostTable.propTypes = {
  source: PropTypes.object.isRequired,
  posts: PropTypes.object.isRequired,
  editingId: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

PostTable.defaultProps = { editingId: null };

function RecordTable({ rows, data }) {
  if (!rows.length) return null;

  return (
    <Scroller>
      <Table>
        <tbody>
          {rows.map((row) => {
            const ValueTd = row.wrap ? WrapTd : Td;
            return (
              <Row key={row.label}>
                <LabelTd>{row.label}</LabelTd>
                <ValueTd>
                  {row.link
                    ? <Link href={row.link(data)} target="_blank" rel="noopener noreferrer">{row.text}</Link>
                    : row.text}
                </ValueTd>
              </Row>
            );
          })}
        </tbody>
      </Table>
    </Scroller>
  );
}

RecordTable.propTypes = {
  rows: PropTypes.array.isRequired,
  data: PropTypes.object.isRequired,
};

// The suggestions one web record holds, a table each under the origin that
// offered it — an origin can offer several, as AI does for a page with three
// bylines. Grouped rather than flattened into one table because a value only
// means anything next to its origin — the page's own title and AI's guess at it
// are different claims, not two rows of the same list.
function SuggestionGroups({ label, suggestions }) {
  const groups = (suggestions || [])
    .map((suggestion) => ({
      key: suggestionKey(suggestion),
      origin: suggestion.src || WEB,
      suggestion,
      rows: suggestionRows(suggestion),
    }))
    .filter((group) => group.rows.length);

  if (!groups.length) return null;

  return (
    <>
      <SubLabel>{label}</SubLabel>
      <CardRow>
        {groups.map(({ key, origin, suggestion, rows }) => (
          <Card key={key}>
            <OriginLabel>{sourceName(origin)}</OriginLabel>
            <RecordTable rows={rows} data={suggestion} />
          </Card>
        ))}
      </CardRow>
    </>
  );
}

SuggestionGroups.propTypes = {
  label: PropTypes.string.isRequired,
  suggestions: PropTypes.array,
};

SuggestionGroups.defaultProps = { suggestions: null };

// One source's record of one URL. Which table it gets follows the record's
// shape: the web record holds suggestions, a discussion source holds posts,
// everything else is a set of properties.
function SourceBlock({
  sourceKey, data, editingId, onEdit, onSave, onRemove, onRefresh,
}) {
  const postSource = POST_SOURCES.find((source) => source.key === sourceKey);
  const recordSource = RECORD_SOURCES.find((source) => source.key === sourceKey);

  let body = null;
  if (sourceKey === WEB) {
    body = (
      <>
        <SuggestionGroups label="Items" suggestions={data.items} />
        <SuggestionGroups label="Authors" suggestions={data.authors} />
      </>
    );
  } else if (postSource) {
    if (data.posts) {
      body = (
        <PostTable
          source={postSource}
          posts={data.posts}
          editingId={editingId}
          onEdit={(id) => onEdit(sourceKey, id)}
          onSave={(id, patch) => onSave(sourceKey, id, patch)}
          onRemove={(id) => onRemove(postSource, id)}
          onRefresh={(id) => onRefresh(sourceKey, id)}
        />
      );
    }
  } else {
    body = <RecordTable rows={recordSource ? recordRows(recordSource, data) : genericRows(data)} data={data} />;
  }

  return (
    <SourceBox>
      <SourceHeading sourceKey={sourceKey} data={data} />
      {body}
    </SourceBox>
  );
}

SourceBlock.propTypes = {
  sourceKey: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  editingId: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

SourceBlock.defaultProps = { editingId: null };

// Takes a discussion's link; the server tells which source it belongs to.
function AddPostForm({ onAdd, onCancel }) {
  const [link, setLink] = useState('');
  const [adding, setAdding] = useState(false);
  const [failed, setFailed] = useState(null);
  useEscape(onCancel);

  const submit = () => {
    if (!link.trim() || adding) return;
    setAdding(true);
    setFailed(null);
    onAdd(link.trim()).catch((e) => {
      setFailed(e.message);
      setAdding(false);
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', mt: '10px', pl: '24px',
    }}>
      <CellInput
        type="url"
        measure={380}
        value={link}
        onChange={(e) => setLink(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={LINK_PLACEHOLDER}
        aria-label={LINK_PLACEHOLDER}
        disabled={adding}
        autoFocus
      />
      <Action type="button" onClick={submit} disabled={adding || !link.trim()}>
        {adding ? 'Adding…' : 'Add'}
      </Action>
      <Action type="button" onClick={onCancel} disabled={adding}>Cancel</Action>
      {failed && <Folded sx={{ color: ERROR }}>{failed}</Folded>}
    </Box>
  );
}

AddPostForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

// Everything one URL holds, source by source, under the URL itself.
function UrlSection({
  urlId, url, extra, confirm, onRefresh, onAddPost, onUpdatePost, onRemovePost, onRefreshPosts,
}) {
  const [open, setOpen] = useState(true);
  const [refreshing, setRefreshing] = useState(null);
  const [failed, setFailed] = useState(null);
  const [adding, setAdding] = useState(false);
  // The post open for editing, as `{ source, id }`.
  const [editing, setEditing] = useState(null);
  const sources = orderedSources(extra);

  const refresh = (ai) => {
    setRefreshing(ai ? 'ai' : 'plain');
    setFailed(null);
    onRefresh(urlId, ai)
      .catch((e) => setFailed(e.message))
      .finally(() => setRefreshing(null));
  };

  // Resolves to whether the source answered for every post asked about.
  const refreshPosts = (source, id) => {
    setFailed(null);
    return onRefreshPosts(urlId, source, id)
      .then(({ failed: kept }) => {
        if (kept.length) setFailed(keptMessage(source, kept));
        return !kept.length;
      })
      .catch((e) => {
        setFailed(e.message);
        return false;
      });
  };

  // The add reads the post from its source; one the source can't answer for
  // opens for its figures to be typed in.
  const addPost = (link) => onAddPost(urlId, { link }).then(({ source, id, failed: unread }) => {
    setAdding(false);
    setFailed(null);
    if (unread.length) {
      setFailed(`${sourceName(source)}: couldn't read ${id} (${unread[0].error}) — fill in its figures`);
      setEditing({ source, id });
    }
  });

  const editPost = (source, id) => setEditing(id ? { source, id } : null);

  const savePost = (source, id, patch) => onUpdatePost(urlId, source, id, patch)
    .then(() => setEditing(null));

  const removePost = async (source, id) => {
    const ok = await confirm({
      title: 'Remove post?',
      message: `${sourceName(source.key)} ${source.idLabel.toLowerCase()} ${id} and its figures will be removed from this page's source data, for every item linking to it. `
        + 'A refresh records it again if the source still finds it.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setFailed(null);
    await onRemovePost(urlId, source.key, id).catch((e) => setFailed(e.message));
  };

  return (
    <Box sx={{ mt: '20px' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
      }}>
        <Toggle
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          aria-label={open ? 'Collapse source data for this URL' : 'Expand source data for this URL'}
        >
          <ExpandMoreIcon sx={{
            fontSize: 18,
            color: MUTED,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .15s',
          }} />
        </Toggle>
        {url
          ? <UrlLink href={url} target="_blank" rel="noopener noreferrer">{url}</UrlLink>
          : <MissingUrl>{urlId}</MissingUrl>}
        {!open && <Folded>{sources.map(blockLabel).join(' · ')}</Folded>}
        {open && url && (
          <>
            <Action
              type="button"
              onClick={() => refresh(false)}
              disabled={Boolean(refreshing)}
              title="Read this page from its sources again"
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
              {refreshing === 'plain' ? 'Reading…' : 'Refresh'}
            </Action>
            <Action
              type="button"
              onClick={() => refresh(true)}
              disabled={Boolean(refreshing)}
              title="Read this page again and have AI read it too — a paid call"
            >
              <AutoAwesomeIcon sx={{ fontSize: 13 }} />
              {refreshing === 'ai' ? 'Reading…' : 'With AI'}
            </Action>
          </>
        )}
        {open && (
          <Action
            type="button"
            onClick={() => setAdding(true)}
            disabled={adding}
            title="Record a Hacker News, Reddit, Lobsters or X discussion of this page"
          >
            <AddIcon sx={{ fontSize: 14 }} />
            Add post
          </Action>
        )}
        {failed && <Folded sx={{ color: ERROR }}>{failed}</Folded>}
      </Box>
      {open && adding && <AddPostForm onAdd={addPost} onCancel={() => setAdding(false)} />}
      {open && (
        <Nest>
          {sources.map((key) => (
            <SourceBlock
              key={key}
              sourceKey={key}
              data={extra[key]}
              editingId={editing?.source === key ? editing.id : null}
              onEdit={editPost}
              onSave={savePost}
              onRemove={removePost}
              onRefresh={refreshPosts}
            />
          ))}
        </Nest>
      )}
    </Box>
  );
}

UrlSection.propTypes = {
  urlId: PropTypes.string.isRequired,
  url: PropTypes.string,
  extra: PropTypes.object.isRequired,
  confirm: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onAddPost: PropTypes.func.isRequired,
  onUpdatePost: PropTypes.func.isRequired,
  onRemovePost: PropTypes.func.isRequired,
  onRefreshPosts: PropTypes.func.isRequired,
};

UrlSection.defaultProps = { url: null };

function Score({ label, score, norm, sortKey }) {
  if (score == null) return null;
  return (
    <Box
      component="span"
      title={`norm ${norm.toFixed(3)} · sort key ${Number(sortKey).toLocaleString()}`}
      sx={{ fontFamily: SANS_STACK, fontSize: '.8rem', color: TEXT_SECONDARY }}
    >
      {`${label} `}
      <Box component="span" sx={{ fontWeight: 700, color: TEXT_BODY }}>{score.toFixed(1)}</Box>
    </Box>
  );
}

Score.propTypes = {
  label: PropTypes.string.isRequired,
  score: PropTypes.number,
  norm: PropTypes.number,
  sortKey: PropTypes.number,
};

Score.defaultProps = { score: null, norm: null, sortKey: null };

// The item's own scores, rolled up from every URL below: read off the item the
// page holds rather than recomputed, so they are what the grids rank by. The
// worker recalculates them after a change to a URL's source data; `onRefresh`
// reloads the item to show that.
function ItemScores({ item, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const extra = item?.extra || {};
  const stats = extra.stats || {};
  const {
    heatScore, buzzScore, heatNorm, buzzNorm,
  } = getHeatBuzz(item || {});

  // A source the item is on can have no figures — a repo with no stars.
  const sources = SOURCE_ORDER
    .filter((key) => SCORE_METRICS[key] && (stats[key] || extra.sources?.includes(key)))
    .map((key) => ({
      key,
      metrics: SCORE_METRICS[key].filter(([metric]) => stats[key]?.[metric] != null),
    }));
  const topPosts = (extra.links || [])
    .filter((link) => DISCUSSION_LINK_SOURCES[link.type] && link.url);

  const refresh = () => {
    setRefreshing(true);
    Promise.resolve(onRefresh()).finally(() => setRefreshing(false));
  };

  return (
    <SourceBox sx={{ mt: '16px' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
      }}>
        <Box sx={{
          fontFamily: SANS_STACK, fontSize: '.85rem', fontWeight: 700, color: TEXT_BODY,
        }}>
          Item scores
        </Box>
        <Score label="Heat" score={heatScore} norm={heatNorm} sortKey={extra.heatSort} />
        <Score label="Buzz" score={buzzScore} norm={buzzNorm} sortKey={extra.buzzSort} />
        {onRefresh && (
          <Action
            type="button"
            onClick={refresh}
            disabled={refreshing}
            title="Load the item's scores again, as the worker last recalculated them"
          >
            <RefreshIcon sx={{ fontSize: 14 }} />
            {refreshing ? 'Loading…' : 'Refresh'}
          </Action>
        )}
      </Box>
      {!sources.length && heatScore == null && <Note>Not scored yet.</Note>}
      {sources.length > 0 && (
        <Scroller>
          <Table>
            <thead>
              <tr>
                <Th>Source</Th>
                <Th>Metric</Th>
                <Th>Value</Th>
                <Th title="log10(value) / log10(the highest any URL holds); Reddit and Lobsters carry no weight">
                  Norm
                </Th>
              </tr>
            </thead>
            <tbody>
              {sources.map(({ key, metrics }) => {
                const rows = metrics.length ? metrics : [[null, null]];
                return rows.map(([metric, label], index) => (
                  <Row key={`${key}-${metric}`}>
                    {index === 0 && (
                      <Td rowSpan={rows.length} sx={{ verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {sourceLogo(key) && <img src={sourceLogo(key)} alt="" style={{ width: 14, height: 14 }} />}
                          {sourceName(key)}
                        </Box>
                      </Td>
                    )}
                    <LabelTd>{label || 'No figures'}</LabelTd>
                    <Td>{metric ? num(stats[key][metric]) : ''}</Td>
                    <Td>{metric && stats[key][`${metric}Norm`] != null ? stats[key][`${metric}Norm`].toFixed(3) : ''}</Td>
                  </Row>
                ));
              })}
            </tbody>
          </Table>
        </Scroller>
      )}
      {topPosts.length > 0 && (
        <Box sx={{
          display: 'flex', gap: '10px', flexWrap: 'wrap', mt: '10px', fontFamily: SANS_STACK, fontSize: '.8rem',
        }}>
          <Box component="span" sx={{ color: TEXT_SECONDARY }}>Top discussions</Box>
          {topPosts.map((link) => (
            <Link key={`${link.type}-${link.id}`} href={link.url} target="_blank" rel="noopener noreferrer">
              {`${sourceName(DISCUSSION_LINK_SOURCES[link.type])} ${link.id}`}
            </Link>
          ))}
        </Box>
      )}
    </SourceBox>
  );
}

ItemScores.propTypes = {
  item: PropTypes.object,
  onRefresh: PropTypes.func,
};

ItemScores.defaultProps = { item: null, onRefresh: null };

/**
 * The item's scores, then every item URL's extras, a section per URL. The page
 * holds the extras, since Look up reads them too; the panel asks for them when
 * first opened.
 */
export default function ItemSourceData({
  item, extras, status, error, onLoad, onRefresh, onAddPost, onUpdatePost, onRemovePost, onRefreshPosts,
  onRefreshScores,
}) {
  const [open, setOpen] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const toggle = () => {
    setOpen((wasOpen) => !wasOpen);
    if (status === 'idle') onLoad();
  };

  // A URL whose extras hold nothing has nothing to head a section with.
  const sections = (extras || []).filter(({ extra }) => Object.keys(extra || {}).length);
  const isEmpty = status === 'loaded' && !sections.length;

  return (
    <Box sx={{ mt: '22px', pt: '18px', borderTop: `1px solid ${HAIRLINE}` }}>
      <Toggle type="button" onClick={toggle}>
        <SectionLabel sx={{ mb: 0 }}>Source data</SectionLabel>
        <ExpandMoreIcon sx={{
          fontSize: 18,
          color: MUTED,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform .15s',
        }} />
      </Toggle>

      {open && (
        <>
          <ItemScores item={item} onRefresh={onRefreshScores} />
          {status === 'loading' && <Note>Loading…</Note>}
          {status === 'failed' && <Note>Could not load source data: {error}</Note>}
          {isEmpty && <Note>No external source has recorded this item.</Note>}
          {sections.map(({ urlId, url, extra }) => (
            <UrlSection
              key={urlId}
              urlId={urlId}
              url={url}
              extra={extra}
              confirm={confirm}
              onRefresh={onRefresh}
              onAddPost={onAddPost}
              onUpdatePost={onUpdatePost}
              onRemovePost={onRemovePost}
              onRefreshPosts={onRefreshPosts}
            />
          ))}
        </>
      )}
      {confirmDialog}
    </Box>
  );
}

ItemSourceData.propTypes = {
  item: PropTypes.object,
  extras: PropTypes.array,
  status: PropTypes.string.isRequired,
  error: PropTypes.string,
  onLoad: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onAddPost: PropTypes.func.isRequired,
  onUpdatePost: PropTypes.func.isRequired,
  onRemovePost: PropTypes.func.isRequired,
  onRefreshPosts: PropTypes.func.isRequired,
  onRefreshScores: PropTypes.func,
};

ItemSourceData.defaultProps = {
  item: null, extras: null, error: null, onRefreshScores: null,
};
